import {
  type LocalizedFieldDto,
  type VehicleExtraOption,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import { getBookedExtras } from '@/utils/pricing.utils';

export type CrossborderCountryLabelOverrides = Record<string, LocalizedFieldDto>;

type CrossborderExtraOptionDetails = {
  permittedDrivingCountries?: string[];
  countryLabelOverrides?: CrossborderCountryLabelOverrides;
};

export function getCrossborderTrueOption(pricing?: Pricing | null): VehicleExtraOption | undefined {
  return (
    (pricing ? getBookedExtras(pricing) : []).find(
      (extra) =>
        extra.vehicleExtraSnapshot.type === VehicleExtraType.CROSSBORDER &&
        extra.vehicleExtraOptionSnapshot.value === 'true',
    )?.vehicleExtraOptionSnapshot ??
    pricing?.availableExtras
      ?.find((extra) => extra.type === VehicleExtraType.CROSSBORDER)
      ?.options.find((option) => option.value === 'true')
  );
}

type VehicleExtraOptionWithDetails = VehicleExtraOption & {
  details?: CrossborderExtraOptionDetails;
};

function getCrossborderOptionDetails(
  pricing?: Pricing | null,
): CrossborderExtraOptionDetails | undefined {
  return (getCrossborderTrueOption(pricing) as VehicleExtraOptionWithDetails | undefined)?.details;
}

export function getCrossborderInfoCountries(pricing?: Pricing | null): string[] {
  const details = getCrossborderOptionDetails(pricing);
  return details?.permittedDrivingCountries ?? [];
}

export function getCrossborderLabelOverrides(
  pricing?: Pricing | null,
): CrossborderCountryLabelOverrides | undefined {
  return getCrossborderOptionDetails(pricing)?.countryLabelOverrides;
}

function resolveCountryLabel(
  countryCode: string,
  locale: string,
  labelOverrides?: CrossborderCountryLabelOverrides,
): string {
  const normalizedCode = countryCode.toUpperCase();
  const language = locale.split('-')[0] as keyof LocalizedFieldDto;
  const override = labelOverrides?.[normalizedCode]?.[language]?.trim();
  if (override) {
    return override;
  }
  const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
  return displayNames.of(normalizedCode) ?? normalizedCode;
}

export function formatCountryNameList(countryNames: string[]): string {
  if (countryNames.length === 0) return '';
  if (countryNames.length === 1) return countryNames[0];
  if (countryNames.length === 2) return `${countryNames[0]} & ${countryNames[1]}`;
  return `${countryNames.slice(0, -1).join(', ')} & ${countryNames[countryNames.length - 1]}`;
}

export function formatCountryCodes(
  countryCodes: string[],
  pricing: Pricing,
  locale: string,
): string {
  const countryNames = countryCodes.map((code) =>
    resolveCountryLabel(code, locale, getCrossborderLabelOverrides(pricing)),
  );
  return formatCountryNameList(countryNames);
}
